// PanelEntityView — the right panel's FOCUSED body: the entity's CONTENT, per
// kind (right-panel focus model). Every reference in the chat opens here
// (`show-in-panel`); the library is the header's explicit "Open in library".
//
//   material → the two-lens view (rightPanel/MaterialFocus): "Is it
//              grounded?" / "Does it teach well?" over the material, which
//              leads with its generation side view while it is being made;
//   source   → where it is used, then its text with the quoted passages
//              marked (rightPanel/SourceFocus), opened at a page span target;
//   export   → the package view (`ExportView`);
//   file     → its metadata card.
//
// Reuses the library detail components (ResourceDetail.tsx), never forks them.
// A link inside the panel (a material's "Created from" source, a package's
// lineage) re-focuses the panel rather than leaving the chat. Review items
// jump within the panel (to the element, or to the claim's source passage),
// never to the editor, which chat never opens (owner rule, 2026-10-01).

import type { ReactNode } from "react";
import type {
    Claim,
    ClaimIntents,
} from "../../components/ClaimRow/ClaimRow.js";
import {
    elementFocusTarget,
    focusElementOf,
} from "../../components/Navigation/focusTarget.js";
import type {
    FocusTarget,
    MaterialRef,
    ResourceRef,
    SourceRef,
} from "../../components/Navigation/types.js";
import type {
    MaterialQuality,
    StatementSource,
} from "../../components/QualitySummary/QualitySummary.js";
import type { StatementOrigin } from "../frame/types.js";
import type { AppPorts } from "../ports.js";
import type { SourceReadStatus } from "../sourceReadStatus.js";
import { FileDetail, PackageDetail } from "./ResourceDetail.js";
import type { ChatWorld } from "./rightPanel/home.js";
import {
    MaterialFocus,
    type MaterialGoalLens,
} from "./rightPanel/MaterialFocus.js";
import { SourceFocus } from "./rightPanel/SourceFocus.js";

export type PanelEntityViewProps = {
    readonly entity: ResourceRef;
    readonly target: FocusTarget | undefined;
    // The claim a focused source was reached from (its step back), if any.
    readonly origin: StatementOrigin | undefined;
    // The chat's world: where a focused source is used.
    readonly world: ChatWorld;
    readonly ports: Pick<
        AppPorts,
        | "loadSource"
        | "loadMaterial"
        | "loadExport"
        | "loadFile"
        | "downloadExport"
    >;
    // A material's quality read: the panel's one live read (null until it
    // lands), shared with the home so the two never disagree.
    readonly qualityOf: (materialId: string) => MaterialQuality | null;
    // A source's live read status (the Library's, FR-3.3); absent ⇒ none shown.
    readonly readStatusOf?: (sourceId: string) => SourceReadStatus | undefined;
    // Open the source passage behind one claim of a material.
    readonly onShowStatementSource: (
        material: MaterialRef,
        row: StatementSource,
    ) => void;
    // The generation side view for this material, or null (no skeleton/job).
    readonly generation: ReactNode | null;
    // Focus another entity in the panel (a link inside the focused one),
    // optionally at a place in it (a citation's page).
    readonly onShowInPanel: (ref: ResourceRef, target?: FocusTarget) => void;
    // Jump to (and flash) the chat replies that cite a source.
    readonly onShowReplies: (entryIds: readonly string[]) => void;
    // Ground a source in this chat (a source from "other files").
    readonly onAttachSource?: (source: SourceRef) => void;
    // A source whose stored content is missing: re-upload its original file
    // INTO it; `onRestored` re-reads the panel once it is back.
    readonly onReuploadSource?: (
        source: SourceRef,
        onRestored: () => void,
    ) => void;
    // The chat's goals a focused material is read against (its third lens).
    readonly goal?: MaterialGoalLens;
    // The knowledge bank's claims: a focused source's, and what a claim
    // offers. Absent ⇒ no claims are shown.
    readonly claims?: {
        readonly ofSource: (sourceId: string) => readonly Claim[];
        readonly intents: ClaimIntents;
    };
};

export const PanelEntityView = ({
    entity,
    target,
    ports,
    generation,
    origin,
    world,
    qualityOf,
    readStatusOf,
    onShowInPanel,
    onShowStatementSource,
    onShowReplies,
    onAttachSource,
    onReuploadSource,
    goal,
    claims,
}: PanelEntityViewProps): ReactNode => {
    switch (entity.type) {
        case "material":
            return (
                <MaterialFocus
                    key={`${entity.id}:${focusElementOf(target) ?? ""}`}
                    material={entity}
                    quality={qualityOf(entity.id)}
                    target={target}
                    loadMaterial={ports.loadMaterial}
                    generation={generation}
                    onShowStatementSource={(row) =>
                        onShowStatementSource(entity, row)
                    }
                    {...(claims === undefined
                        ? {}
                        : { claimIntents: claims.intents })}
                    onOpenRef={onShowInPanel}
                    {...(goal === undefined ? {} : { goal })}
                />
            );
        case "source":
            return (
                <SourceFocus
                    key={entity.id}
                    source={entity}
                    target={target}
                    origin={origin}
                    world={world}
                    readStatus={readStatusOf?.(entity.id)}
                    loadSource={ports.loadSource}
                    onShowStatement={(material, claimId) =>
                        onShowInPanel(material, elementFocusTarget(claimId))
                    }
                    onShowReplies={onShowReplies}
                    onAttach={onAttachSource}
                    onReupload={onReuploadSource}
                    {...(claims === undefined
                        ? {}
                        : {
                              claims: {
                                  list: claims.ofSource(entity.id),
                                  intents: claims.intents,
                              },
                          })}
                />
            );
        case "export":
            return (
                <PackageDetail
                    key={entity.id}
                    id={entity.id}
                    title={entity.title}
                    loadExport={ports.loadExport}
                    downloadExport={ports.downloadExport}
                    onOpenRef={onShowInPanel}
                />
            );
        case "file":
            return (
                <FileDetail
                    key={entity.id}
                    id={entity.id}
                    title={entity.title}
                    loadFile={ports.loadFile}
                />
            );
    }
};
